import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, CheckCircle, XCircle, Timer } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const MathBlitz = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const grade = user?.grade_level || 7;
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [problem, setProblem] = useState(null);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [streak, setStreak] = useState(0);
  const [difficulty, setDifficulty] = useState(1);

  const generateProblem = useCallback(() => {
    const ops = ['+', '-', '*', '/'];
    // Grade-aware difficulty: increases faster for higher grades
    const effectiveDifficulty = difficulty + (grade >= 8 ? 3 : grade >= 7 ? 1 : 0);
    const availableOps = effectiveDifficulty > 2 ? ops : ops.slice(0, 3);
    const op = availableOps[Math.floor(Math.random() * availableOps.length)];
    let a, b, answer;
    const max = grade >= 8 ? 50 + effectiveDifficulty * 10 : grade >= 7 ? 20 + effectiveDifficulty * 8 : 10 + effectiveDifficulty * 5;
    
    switch (op) {
      case '+': a = Math.floor(Math.random() * max) + (grade >= 8 ? 20 : 1); b = Math.floor(Math.random() * max) + 1; answer = a + b; break;
      case '-': a = Math.floor(Math.random() * max) + (grade >= 8 ? 30 : 10); b = Math.floor(Math.random() * a) + 1; answer = a - b; break;
      case '*': a = Math.floor(Math.random() * (grade >= 8 ? 20 : 12)) + 2; b = Math.floor(Math.random() * (grade >= 8 ? 15 : 12)) + 2; answer = a * b; break;
      case '/': b = Math.floor(Math.random() * 10) + 2; answer = Math.floor(Math.random() * 12) + 2; a = b * answer; break;
      default: a = 1; b = 1; answer = 2;
    }
    
    const wrongAnswers = new Set();
    while (wrongAnswers.size < 3) {
      const offset = Math.floor(Math.random() * 10) - 5;
      const wrong = answer + (offset === 0 ? 1 : offset);
      if (wrong !== answer && wrong >= 0) wrongAnswers.add(wrong);
    }
    
    const allOptions = [...wrongAnswers, answer].sort(() => Math.random() - 0.5);
    setProblem({ a, b, op, answer });
    setOptions(allOptions);
    setFeedback(null);
  }, [difficulty]);

  useEffect(() => { generateProblem(); }, [generateProblem]);

  useEffect(() => {
    if (timeLeft <= 0 || gameOver) { setGameOver(true); return; }
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, gameOver]);

  const handleAnswer = (selected) => {
    if (feedback || gameOver) return;
    const correct = selected === problem.answer;
    if (correct) {
      const points = 10 + streak * 2;
      setScore(s => s + points);
      setStreak(s => s + 1);
      if (streak > 0 && streak % 5 === 0) setDifficulty(d => d + 1);
      setFeedback({ correct: true, msg: streak >= 3 ? `${streak + 1}x Combo! +${points}pts` : `Correct! +${points}pts` });
    } else {
      setStreak(0);
      setFeedback({ correct: false, msg: `The answer was ${problem.answer}` });
    }
    setTimeout(() => generateProblem(), 800);
  };

  if (gameOver) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-2xl max-w-md w-full text-center">
          <div className="text-7xl mb-4">🎯</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Time's Up!</h2>
          <p className="text-6xl font-black text-[#4D96FF] mb-6">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">points scored</p>
          <div className="space-y-3">
            <button onClick={() => { setScore(0); setTimeLeft(60); setGameOver(false); setStreak(0); setDifficulty(1); generateProblem(); }} className="w-full py-4 bg-[#4D96FF] text-white rounded-2xl font-bold text-lg border-b-4 border-[#3B7BE0] active:border-b-0 active:translate-y-1 transition-all" data-testid="play-again-btn">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold text-lg" data-testid="back-to-arena-btn">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="math-blitz-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] hover:text-[#1E293B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex items-center gap-4">
            <div className="bg-orange-500 text-white px-4 py-2 rounded-2xl font-bold flex items-center gap-2"><Timer className="w-5 h-5" />{timeLeft}s</div>
            <div className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold"><Zap className="w-5 h-5 inline mr-1" />{score}pts</div>
          </div>
        </div>
        {streak >= 3 && <div className="text-center mb-4 text-2xl font-black text-orange-500 animate-bounce">{streak}x STREAK!</div>}
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl text-center mb-6">
          <p className="text-sm font-bold text-[#64748B] mb-2 uppercase tracking-wider">Solve this!</p>
          <p className="text-6xl font-black text-[#1E293B]">{problem?.a} {problem?.op} {problem?.b} = ?</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {options.map((opt, i) => (
            <button key={i} data-testid={`answer-${i}`} onClick={() => handleAnswer(opt)}
              className={`p-6 rounded-2xl text-3xl font-black border-2 transition-all ${
                feedback && opt === problem.answer ? 'bg-green-100 border-green-500 text-green-700' :
                feedback && opt !== problem.answer ? 'bg-slate-50 border-slate-200 text-slate-400' :
                'bg-white border-slate-200 hover:border-[#4D96FF] hover:bg-blue-50 text-[#1E293B] active:scale-95'
              }`}>{opt}</button>
          ))}
        </div>
        {feedback && (
          <div className={`mt-4 p-4 rounded-2xl text-center font-bold text-lg ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {feedback.correct ? <CheckCircle className="w-6 h-6 inline mr-2" /> : <XCircle className="w-6 h-6 inline mr-2" />}
            {feedback.msg}
          </div>
        )}
      </div>
    </div>
  );
};

export default MathBlitz;
